import {
  useNotificationsQuery,
  Notification,
  useMarkNotificationAsReadMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { Popover } from "@headlessui/react";
import { Transition } from "@headlessui/react";
import { NotificationItem } from "./NotificationItem";
import { useQueryClient } from "@tanstack/react-query";
import Loading from "../Loading";
import { useWindowSize } from "@/src/hooks/useWindowSize";
import { classNames } from "@/src/utils/cn";
import { useRef, useState, useEffect, useCallback } from "react";

export function NotificationPanel({
  isCollapsed = false,
  isMobile = false,
}: {
  isCollapsed?: boolean;
  isMobile?: boolean;
}) {
  const [cursor, setCursor] = useState<string>("");
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const client = useGqlClient();
  const queryClient = useQueryClient();

  // Reset state when panel is mounted
  useEffect(() => {
    setCursor("");
    setAllNotifications([]);
    setIsLoadingMore(false);
    queryClient.invalidateQueries({
      queryKey: ["Notifications"],
    });
  }, []); // Empty deps array means this runs once when mounted

  const notifications = useNotificationsQuery(client, {
    input: {
      cursor: cursor,
      limit: 10,
    },
  });

  useEffect(() => {
    if (notifications.data) {
      if (cursor === "") {
        // Initial load
        setAllNotifications(
          notifications.data.notifications
            .notifications as unknown as Notification[],
        );
      } else {
        // Append new notifications
        setAllNotifications((prev) => [
          ...prev,
          ...(notifications.data.notifications
            .notifications as unknown as Notification[]),
        ]);
      }
      setIsLoadingMore(false);
    }
  }, [notifications.data, cursor]);

  const markNotificationAsRead = useMarkNotificationAsReadMutation(client);

  const handleScroll = useCallback(() => {
    if (
      !containerRef.current ||
      isLoadingMore ||
      !notifications.data?.notifications.hasMore
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const scrollThreshold = 20; // pixels from bottom

    if (scrollHeight - (scrollTop + clientHeight) < scrollThreshold) {
      setIsLoadingMore(true);
      setCursor(notifications.data.notifications.cursor);
    }
  }, [isLoadingMore, notifications.data]);

  const { width } = useWindowSize();
  const defaultWidth = width ? width : 300;
  const mobileWidth = `w-[${defaultWidth}px]`;

  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Popover.Panel
        className={
          isMobile
            ? "absolute mt-1 z-50 bg-white rounded-md shadow-md -bottom-4 right-0"
            : `fixed mt-1 z-50 bg-white rounded-md shadow-md top-auto bottom-12 ${isCollapsed ? "left-[80px]" : "left-[280px]"}`
        }
      >
        <div
          className={classNames(
            ` md:w-[600px] md:max-w-700px max-h-[600px] overflow-y-auto rounded-md `,
            mobileWidth,
          )}
          ref={containerRef}
          onScroll={handleScroll}
        >
          {allNotifications.filter((n) => !n.readAt).length > 0 ? (
            <>
              <div className="z-20 sticky top-0 px-3 py-2 flex items-center justify-between bg-white">
                <p className="font-semibold text-blue-600 text-sm">
                  New for you
                </p>
                <button
                  className="text-sm text-gray-400 hover:text-gray-500"
                  onClick={async () => {
                    const unread = allNotifications.filter((n) => !n.readAt);

                    // Mark all as read
                    await Promise.all(
                      unread.map((n) =>
                        markNotificationAsRead.mutateAsync({
                          id: n.id,
                        }),
                      ),
                    );

                    // Reset cursor and notifications to refetch from beginning
                    setCursor("");
                    setAllNotifications([]);

                    // Invalidate all notification queries
                    await queryClient.invalidateQueries({
                      queryKey: ["Notifications"],
                    });
                  }}
                >
                  Mark all read
                </button>
              </div>
            </>
          ) : null}
          <NotificationsInner
            notifications={allNotifications}
            isLoading={notifications.isLoading}
            error={notifications.error}
          />
          {isLoadingMore && (
            <div className="p-3 flex justify-center items-center">
              <Loading />
            </div>
          )}
        </div>
      </Popover.Panel>
    </Transition>
  );
}

function NotificationsInner(props: {
  notifications: Notification[];
  isLoading: boolean;
  error: unknown;
}) {
  if (props.error) {
    return (
      <div className="p-3">
        <p className="text-sm text-gray-400">Error loading notifications</p>
      </div>
    );
  }
  if (props.isLoading && props.notifications.length === 0) {
    return (
      <div className="p-3">
        <Loading />
      </div>
    );
  }

  // Check if there are no notifications
  if (props.notifications.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-5 5v-5zM11 19H6a2 2 0 01-2-2V7a2 2 0 012-2h5m5 0v5a2 2 0 002 2h5M9 7h1m-1 4h1m-1 4h1"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700">
              No notifications
            </p>
            <p className="text-sm text-gray-500 mt-1">
              New notifications will appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div>
        {props.notifications
          .filter((n) => !n.readAt)
          .map((notification, i) => {
            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                isLastUnread={
                  i === props.notifications.filter((n) => !n.readAt).length - 1
                }
                isLast={i === props.notifications.length - 1}
              />
            );
          })}
      </div>
      <p className="text-sm font-semibold text-gray-700 p-3 bg-gray-50">
        Previous notifications
      </p>
      <div>
        {props.notifications
          .filter((n) => n.readAt)
          .map((notification, i) => {
            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                isLastUnread={false}
                isLast={i === props.notifications.length - 1}
              />
            );
          })}
      </div>
    </div>
  );
}
